import { useQuery } from '@tanstack/react-query';

import { ScopePills } from '@/components/table/ScopePills';
import { useAuth } from '@/lib/auth/auth-context';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';

import { GymExerciseGroups, GymFoodGroups } from './GymGroups';
import { GymReports, GymSearchMisses } from './GymReports';
import type { GymCatalogSearch, GymCatalogTab } from './gym-catalog-search';

export interface GymCatalogScreenProps {
  readonly search: GymCatalogSearch;
  readonly onSearchChange: (next: GymCatalogSearch) => void;
  /** The console's create page, named for a group; linked back once the row exists. */
  readonly onMakeFood: (name: string, gymFoodIds: readonly string[]) => void;
  readonly onMakeExercise: (name: string, gymExerciseIds: readonly string[]) => void;
  readonly onOpenFood: (foodId: string) => void;
}

const TAB_LABELS: Record<GymCatalogTab, string> = {
  foods: 'Foods',
  exercises: 'Exercises',
  reports: 'Reports',
  misses: 'Not found',
};

const TAB_HELP: Record<GymCatalogTab, string> = {
  foods:
    'Foods gyms added for their own plans, grouped by name across gyms and ranked by how many gyms and members use them. ' +
    'Nothing here is in the shared catalog until you decide.',
  exercises:
    'Exercises gyms added, grouped the same way. Each gym answered which joints it loads and the pregnancy questions; ' +
    'check those before making it a catalog exercise.',
  reports:
    'What gyms say is wrong with catalog foods. Fix the food on its own page, then answer the report here.',
  misses: 'What trainers searched for and found nowhere, across gyms: where the catalog is thin.',
};

/**
 * "From gyms" (Steado for Gyms, D77): Steado's review of what gyms add.
 *
 * Its own screen rather than a view of the food catalog: the catalog's views
 * are filter presets over one endpoint, and these lists come from others.
 * Making a catalog row from a group is the console's own create page (opened
 * by the route), so a catalog food made here is made exactly as any other.
 */
export function GymCatalogScreen({
  search,
  onSearchChange,
  onMakeFood,
  onMakeExercise,
  onOpenFood,
}: GymCatalogScreenProps) {
  const { api } = useAuth();
  const counts = useQuery(scopeCountsQueryOptions(api));
  const tab: GymCatalogTab = search.tab ?? 'foods';
  const waiting = counts.data?.gymCatalog;
  const count: Partial<Record<GymCatalogTab, number>> =
    waiting === undefined
      ? {}
      : { foods: waiting.foods, exercises: waiting.exercises, reports: waiting.reports };

  return (
    <div className="flex max-w-[1180px] flex-col gap-3">
      <ScopePills
        label="From gyms"
        scopes={(Object.keys(TAB_LABELS) as GymCatalogTab[]).map((id) => ({
          id,
          label: TAB_LABELS[id],
          ...(count[id] === undefined ? {} : { count: count[id] }),
        }))}
        value={tab}
        onChange={(next: GymCatalogTab) => {
          onSearchChange({ tab: next });
        }}
      />
      <p className="text-[13px] leading-relaxed text-muted-foreground">{TAB_HELP[tab]}</p>

      {tab === 'foods' ? <GymFoodGroups onMake={onMakeFood} /> : null}
      {tab === 'exercises' ? <GymExerciseGroups onMake={onMakeExercise} /> : null}
      {tab === 'reports' ? (
        <GymReports
          status={search.status ?? 'open'}
          onStatusChange={(status) => {
            onSearchChange({ tab, status });
          }}
          onOpenFood={onOpenFood}
        />
      ) : null}
      {tab === 'misses' ? (
        <GymSearchMisses
          kind={search.kind ?? 'food'}
          onKindChange={(kind) => {
            onSearchChange({ tab, kind });
          }}
        />
      ) : null}
    </div>
  );
}
